/**
 * Unsaved-changes navigation guard for the pricing grid.
 *
 * Two layers of protection, only active when dirtyTracking reports dirty:
 *
 *   1. In-page <a> link clicks — intercepted and replaced with a custom
 *      two-button confirmation modal. Only for same-tab navigations (no
 *      target=_blank, no modifier/middle clicks, no hash-only links).
 *
 *   2. window `beforeunload` — catches tab close, URL-bar change, back/
 *      forward, reload. Browsers deliberately do not allow customizing this
 *      dialog's text or buttons; we only toggle it on/off based on dirty
 *      state so the user sees the browser's generic "leave site?" prompt.
 */

import { isAnyDirty } from "../components/dirtyTracking.js";
import { showCustomConfirm } from "../components/popup.js";

const CONFIRM_MESSAGE =
  "You have quantity changes that are not yet applied to your cart. " +
  "Leave the page and loose those changes?";
const LEFT_BUTTON = "No - Return";
const RIGHT_BUTTON = "Yes - Move Anyway";

/** True if a programmatic navigation is in flight (bypass the guard). */
let bypassGuard = false;

/**
 * Suppress both guard layers for a navigation this code is performing itself —
 * a cart-sync apply reload or a history restore.
 *
 * Without this, a programmatic `location.reload()` fires `beforeunload` exactly
 * like a user-initiated one and the browser shows its generic "Leave site?"
 * dialog whenever any grid input is dirty. Not auto-reset: the page is going
 * away. Pass false only if the navigation was abandoned.
 */
export function setNavigationGuardBypass(bypass: boolean): void {
  bypassGuard = bypass;
}

/** Match hrefs we should never intercept. */
function isSpecialScheme(href: string): boolean {
  return (
    href.startsWith("#") ||
    href.toLowerCase().startsWith("mailto:") ||
    href.toLowerCase().startsWith("tel:") ||
    href.toLowerCase().startsWith("javascript:")
  );
}

/**
 * Decide whether a click event should trigger the guard.
 * Returns false for new-tab clicks, modifier keys, hash-only links, etc.
 */
function shouldIntercept(e: MouseEvent, anchor: HTMLAnchorElement): boolean {
  // Modifier keys or middle/right click → browser handles in a new tab or
  // as a non-navigation; don't interrupt.
  if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return false;
  if (e.button !== 0) return false;

  if (anchor.target && anchor.target !== "" && anchor.target !== "_self") {
    return false;
  }

  const rawHref = anchor.getAttribute("href");
  if (!rawHref) return false;
  if (isSpecialScheme(rawHref)) return false;

  // Same-page hash navigation → not leaving the page.
  try {
    const resolved = new URL(anchor.href, window.location.href);
    const sameDoc =
      resolved.origin === window.location.origin &&
      resolved.pathname === window.location.pathname &&
      resolved.search === window.location.search;
    if (sameDoc && resolved.hash !== window.location.hash) return false;
    if (sameDoc && resolved.hash === window.location.hash) return false;
  } catch {
    // Malformed URL — let the browser handle it.
    return false;
  }

  return true;
}

export function initNavigationGuard(): void {
  // Layer 1 — in-page <a> clicks.
  // Delegated on document so it catches lazily rendered content too.
  document.addEventListener(
    "click",
    function (e: MouseEvent) {
      if (bypassGuard) return;
      if (!isAnyDirty()) return;

      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;

      if (!shouldIntercept(e, anchor)) return;

      e.preventDefault();
      e.stopPropagation();

      const destination = anchor.href;
      showCustomConfirm({
        message: CONFIRM_MESSAGE,
        leftButtonText: LEFT_BUTTON,
        rightButtonText: RIGHT_BUTTON,
        onConfirm: () => {
          // Bypass beforeunload so the user doesn't get a second prompt from
          // the browser for the navigation they just explicitly approved.
          bypassGuard = true;
          window.location.href = destination;
        },
      });
    },
    // Capture phase so we run before page-level click handlers that might
    // call e.stopPropagation() themselves.
    true,
  );

  // Layer 2 — tab close / URL bar / back / reload.
  // Browser will show its own generic dialog; we only control whether it
  // appears at all.
  window.addEventListener("beforeunload", function (e: BeforeUnloadEvent) {
    if (bypassGuard) return;
    if (!isAnyDirty()) return;

    e.preventDefault();
    // Legacy string-assignment form required by some browsers.
    e.returnValue = "";
  });
}
